import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, MIN_PASSWORD_LENGTH, PASSWORD_HINT } from '../api.js';

/**
 * Choosing a new password from an emailed link.
 *
 * The token is in the query string and is the only thing proving the
 * person asked for this, so nothing here needs a session — someone who
 * cannot sign in is exactly who this is for.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= MIN_PASSWORD_LENGTH && password === confirm && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reset the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <div className="plate">
        <div className="hazard hazard-rule" role="presentation" />
        <div className="stack" style={{ padding: '1.5rem' }}>
          <h2>Choose a new password</h2>

          {!token ? (
            <p className="notice">
              That link is missing its token. Ask for a new one from the sign
              in screen.
            </p>
          ) : done ? (
            <>
              <p className="notice ok">
                Password changed. Everything that was signed in as you has
                been signed out.
              </p>
              <button onClick={() => navigate('/')}>Sign in</button>
            </>
          ) : (
            <>
              <div>
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="mono muted" style={{ fontSize: '0.75rem', marginTop: '0.3rem' }}>
                  {PASSWORD_HINT}
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password">New password again</label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit(); }}
                />
              </div>

              {mismatch && <p className="notice">Those two do not match.</p>}
              {error && <p className="notice">{error}</p>}

              <button onClick={() => void submit()} disabled={!canSubmit}>
                Change password
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
